import { useIntl } from 'react-intl';
import { ADMIN_API_KEY_GRANTABLE_PERMISSIONS, permissionScope, type Permission } from '@immo/api-contract';
import { formatPermission, permissionLiteral } from '@immo/i18n';

/**
 * The grouping + labelling helper behind the admin API-key issue dialog's
 * permission catalogue (IMM-435.3).
 *
 * The catalogue is `ADMIN_API_KEY_GRANTABLE_PERMISSIONS` from the contract
 * (every workspace/org permission minus api-key management; `backoffice:*`
 * excluded by construction). Grouping is DERIVED from the permission literal —
 * `permissionScope()` for the two scope groups, the module segment (the 2nd
 * `:` part) for the sub-groups — never a hand-maintained map.
 *
 * Labels come from the SHARED permission catalogue `permissions.<id>` in
 * `@immo/i18n` — the same entries the customer app reads (IMM-620.1) — looked up
 * through a SAFE lookup that falls back to the raw permission identifier when a
 * key is missing: a missing label degrades legibility, never capability, and
 * never throws.
 */

// Only the two workspace/org scopes ever appear in the grantable catalogue.
export type GrantableScope = 'WORKSPACE' | 'ORG';

export type PermissionModuleGroup = {
  readonly module: string;
  readonly permissions: ReadonlyArray<Permission>;
};

export type PermissionScopeGroup = {
  readonly scope: GrantableScope;
  readonly modules: ReadonlyArray<PermissionModuleGroup>;
};

// WORKSPACE first, then ORG — matching the "Workspace-Rechte / Standort-Rechte"
// copy order.
const SCOPE_ORDER: ReadonlyArray<GrantableScope> = ['WORKSPACE', 'ORG'];

// Exported so the catalogue totality guard (catalogue.test.ts) derives the
// module set through the app's own splitter rather than re-implementing the
// `:` split — the derived-set duplication BL-005 warns against.
export function moduleOf(permission: Permission): string {
  const parts = permission.split(':');

  return parts[1] ?? permission;
}

/**
 * Group the grantable catalogue by scope, then by module. Modules appear in the
 * order their first permission does in the catalogue; permissions keep catalogue
 * order within a module. An optional explicit list is accepted for tests.
 */
export function groupAdminGrantablePermissions(
  permissions: ReadonlyArray<Permission> = ADMIN_API_KEY_GRANTABLE_PERMISSIONS,
): ReadonlyArray<PermissionScopeGroup> {
  return SCOPE_ORDER.map((scope) => {
    const scoped = permissions.filter((permission) => permissionScope(permission) === scope);
    const byModule = new Map<string, Permission[]>();

    for (const permission of scoped) {
      const module = moduleOf(permission);
      const bucket = byModule.get(module);

      if (bucket === undefined) byModule.set(module, [permission]);
      else bucket.push(permission);
    }

    return {
      scope,
      modules: [...byModule.entries()].map(([module, modulePermissions]) => ({
        module,
        permissions: modulePermissions,
      })),
    };
  }).filter((group) => group.modules.length > 0);
}

/**
 * A `(permission) => label` lookup that reads the German (or active-locale)
 * label from the SHARED `permissions.*` catalogue and falls back to the raw
 * permission identifier when no label key exists. `intl.messages` carries the
 * flattened dotted keys, so a missing key is a plain absence check — no
 * `formatMessage` throw, no missing-translation Sentry noise for the unlabelled
 * tail.
 *
 * The customer app reads the same catalogue through `permissionLabel()` in
 * `apps/web-app/src/lib/permissions/labels.ts`. One source, two readers: the
 * next permission is labelled once, for both surfaces (IMM-620.1).
 */
export function usePermissionLabel(): (permission: string) => string {
  const intl = useIntl();

  return (permission: string) => {
    const id = `permissions.${permission}`;

    return id in intl.messages ? intl.formatMessage({ id }) : permission;
  };
}

/**
 * A `(permission) => literal | null` lookup for the identifier rendered NEXT to
 * the label — the token the public API docs specify, which is what an operator
 * issuing a key on someone's behalf actually has to match (IMM-620.1).
 *
 * `null` when the label lookup degraded to the literal itself, so the raw
 * identifier prints once rather than twice. The pairing rule lives in
 * `@immo/i18n` beside the catalogue, shared with the customer app's
 * `permissionLiteralOf()`.
 */
export function usePermissionLiteral(): (permission: string) => string | null {
  const label = usePermissionLabel();

  return (permission: string) => permissionLiteral(label(permission), permission);
}

/**
 * The plain-text pairing — `Leads lesen (workspace:lead:lead-read)` — for the
 * contexts that hold a string rather than markup, such as the key detail page's
 * „Berechtigungen" field.
 */
export function useFormatPermission(): (permission: string) => string {
  const label = usePermissionLabel();

  return (permission: string) => formatPermission(label(permission), permission);
}

/**
 * A `(module) => label` lookup for the catalogue's module sub-group headings,
 * reading `pages.admin.api-keys.issue.module.<module>` and falling back to the
 * raw module segment when no label exists — same degrade-never-throw contract as
 * `usePermissionLabel`, so a new module never breaks the heading.
 */
export function useModuleLabel(): (module: string) => string {
  const intl = useIntl();

  return (module: string) => {
    const id = `pages.admin.api-keys.issue.module.${module}`;

    return id in intl.messages ? intl.formatMessage({ id }) : module;
  };
}
